import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0Data } from './Auth0DataContext';
import apiClient from '../lib/api';

const ChatContext = createContext();

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

// Default configuration
const DEFAULT_CONFIG = {
  defaultMessageHistoryLimit: 20,
  maxMessageHistoryLimit: 100,
  model: 'claude-sonnet-4-20250514',
  features: {
    streaming: true,
    tools: true,
    knowledgeBase: true,
    conversations: true,
  },
};

export const ChatProvider = ({ children }) => {
  const { user } = useAuth0Data();

  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Other state
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [messageHistoryLimit, setMessageHistoryLimit] = useState(DEFAULT_CONFIG.defaultMessageHistoryLimit);

  // Load chat data when user changes
  useEffect(() => {
    if (user?.sub) {
      console.log('🔄 User authenticated, loading chat data for:', user.sub);
      loadChatData();
    } else {
      console.log('🔄 User logged out, clearing chat data');
      setConversations([]);
      setActiveConversationId(null);
      setMessages([]);
      setKnowledgeBase([]);
      setChatLoading(false);
    }
  }, [user?.sub]);

  const loadChatData = async () => {
    if (!user?.sub) {
      setChatLoading(false);
      return;
    }

    try {
      setChatLoading(true);
      console.log('🔄 Loading chat data from backend...');

      // Load config, conversations, and knowledge base in parallel
      const [configResult, conversationsResult, kbResult] = await Promise.allSettled([
        apiClient.getChatConfig(),
        apiClient.getConversations(),
        apiClient.getKnowledgeBase(),
      ]);

      // Process config
      if (configResult.status === 'fulfilled' && configResult.value) {
        setConfig(configResult.value);
        setMessageHistoryLimit(configResult.value.defaultMessageHistoryLimit || DEFAULT_CONFIG.defaultMessageHistoryLimit);
        console.log('✅ Chat config loaded:', configResult.value);
      }

      // Process conversations
      if (conversationsResult.status === 'fulfilled' && conversationsResult.value) {
        setConversations(conversationsResult.value);
        console.log(`✅ Loaded ${conversationsResult.value.length} conversations`);

        // Load most recent conversation if available
        if (conversationsResult.value.length > 0) {
          const mostRecent = conversationsResult.value[0];
          await loadConversation(mostRecent.id);
        }
      }

      // Process knowledge base
      if (kbResult.status === 'fulfilled' && kbResult.value) {
        const processedDocs = kbResult.value.map(doc => ({
          id: doc.documentId,
          name: doc.name,
          content: doc.content,
          wordCount: doc.wordCount,
          size: doc.size,
          type: doc.fileType,
          fileTypeInfo: doc.fileTypeInfo,
          metadata: doc.metadata,
          uploadDate: doc.uploadDate,
          processed: doc.processed,
          contentPreview: doc.contentPreview,
        }));
        setKnowledgeBase(processedDocs);
        console.log(`✅ Loaded ${processedDocs.length} knowledge base documents`);
      }

    } catch (error) {
      console.error('❌ Error loading chat data:', error);
      setConversations([]);
      setMessages([]);
      setKnowledgeBase([]);
    } finally {
      setChatLoading(false);
    }
  };

  /**
   * Load a specific conversation's messages
   */
  const loadConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setActiveConversationId(null);
      setMessages([]);
      return;
    }

    try {
      console.log(`📂 Loading conversation: ${conversationId}`);
      const conversationMessages = await apiClient.getConversationMessages(conversationId);

      setActiveConversationId(conversationId);
      setMessages(conversationMessages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
      })));

      console.log(`✅ Loaded ${conversationMessages.length} messages`);
    } catch (error) {
      console.error('❌ Error loading conversation:', error);
    }
  }, []);

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    console.log('🆕 Starting new conversation');
  }, []);

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      console.log(`🗑️ Deleting conversation: ${conversationId}`);
      await apiClient.deleteConversation(conversationId);

      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // If this was the active conversation, clear it
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }

      console.log('✅ Conversation deleted');
    } catch (error) {
      console.error('❌ Error deleting conversation:', error);
      throw error;
    }
  }, [activeConversationId]);

  /**
   * Rename a conversation
   */
  const renameConversation = useCallback(async (conversationId, newTitle) => {
    try {
      console.log(`✏️ Renaming conversation: ${conversationId}`);
      await apiClient.updateConversation(conversationId, newTitle);

      // Update local state
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, title: newTitle } : c
      ));

      console.log('✅ Conversation renamed');
    } catch (error) {
      console.error('❌ Error renaming conversation:', error);
      throw error;
    }
  }, []);

  /**
   * Send a message and stream the response
   */
  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return;

    // Add user message immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setStreamingMessage('');

    // Create placeholder for assistant message
    const assistantMessageId = `assistant-${Date.now() + 1}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };

    setMessages(prev => [...prev, assistantMessage]);

    try {
      let fullResponse = '';
      let newConversationId = activeConversationId;

      await apiClient.streamChat(content, messages, {
        conversationId: activeConversationId,
        messageHistoryLimit,
        onConversationCreated: (convId, title) => {
          console.log(`🆕 New conversation created: ${convId}`);
          newConversationId = convId;
          setActiveConversationId(convId);

          // Add to conversations list
          const newConv = {
            id: convId,
            title,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setConversations(prev => [newConv, ...prev]);
        },
        onContent: (text) => {
          fullResponse += text;
          setStreamingMessage(fullResponse);
          // Update the assistant message in place
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: fullResponse }
              : msg
          ));
        },
        onToolUse: (toolName, input) => {
          console.log(`🔧 Tool called: ${toolName}`, input);
        },
        onToolResult: (toolName, success, error) => {
          console.log(`🔧 Tool result: ${toolName}`, success ? 'success' : `error: ${error}`);
        },
        onDone: (messageId, conversationId) => {
          console.log('✅ Stream complete');

          // Update conversation's updatedAt in local state
          if (conversationId) {
            setConversations(prev => {
              const updated = prev.map(c =>
                c.id === conversationId
                  ? { ...c, updatedAt: new Date().toISOString() }
                  : c
              );
              // Sort by updatedAt desc
              return updated.sort((a, b) =>
                new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            });
          }

          // Mark message as no longer streaming
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, isStreaming: false }
              : msg
          ));
        },
        onError: (error) => {
          console.error('❌ Stream error:', error);
          // Update message with error
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: `Error: ${error.message}`, isStreaming: false, isError: true }
              : msg
          ));
        },
      });

    } catch (error) {
      console.error('❌ Error sending message:', error);
      // Update message with error
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, content: `Error: ${error.message}`, isStreaming: false, isError: true }
          : msg
      ));
    } finally {
      setIsLoading(false);
      setStreamingMessage('');
    }
  }, [messages, messageHistoryLimit, activeConversationId]);

  /**
   * Clear all messages and conversations
   */
  const clearAllHistory = useCallback(async () => {
    try {
      console.log('🗑️ Clearing all chat history...');
      await apiClient.clearChatHistory();
      setConversations([]);
      setActiveConversationId(null);
      setMessages([]);
      console.log('✅ All chat history cleared');
    } catch (error) {
      console.error('❌ Error clearing history:', error);
      throw error;
    }
  }, []);

  /**
   * Add a document to the knowledge base
   */
  const addKnowledgeBaseDocument = useCallback(async (document) => {
    if (!user?.sub) {
      console.error('❌ No user ID available for saving document');
      return;
    }

    console.log('📄 Adding document to knowledge base:', document.name);

    const processedDocument = {
      ...document,
      processed: true,
      contentPreview: document.content?.substring(0, 300).replace(/\s+/g, ' ') || '',
    };

    try {
      await apiClient.createKnowledgeBaseDocument({
        documentId: document.id,
        name: document.name,
        content: document.content,
        wordCount: document.wordCount,
        size: document.size,
        fileType: document.type,
        fileTypeInfo: document.fileTypeInfo,
        metadata: document.metadata,
        uploadDate: document.uploadDate,
        processed: processedDocument.processed,
        contentPreview: processedDocument.contentPreview,
      });

      setKnowledgeBase(prev => [...prev, processedDocument]);

      // Add system message about the upload
      const systemMessage = {
        id: `system-${Date.now()}`,
        role: 'assistant',
        content: `📄 **Document uploaded:** "${document.name}"

I now have access to this document (${document.wordCount?.toLocaleString() || 0} words) and can answer questions about its contents. Just ask!`,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, systemMessage]);
      console.log('✅ Document added successfully');

    } catch (error) {
      console.error('❌ Error saving document:', error);
      throw error;
    }
  }, [user?.sub]);

  /**
   * Remove a document from the knowledge base
   */
  const removeKnowledgeBaseDocument = useCallback(async (documentId) => {
    const documentToRemove = knowledgeBase.find(doc => doc.id === documentId);

    if (!documentToRemove) {
      console.error('❌ Document not found:', documentId);
      return;
    }

    try {
      const dbId = documentToRemove.dbId || documentId;
      await apiClient.deleteKnowledgeBaseDocument(dbId);

      setKnowledgeBase(prev => prev.filter(doc => doc.id !== documentId));

      const systemMessage = {
        id: `system-${Date.now()}`,
        role: 'assistant',
        content: `📄 Document removed: "${documentToRemove.name}"`,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, systemMessage]);
      console.log('✅ Document removed successfully');

    } catch (error) {
      console.error('❌ Error removing document:', error);
      throw error;
    }
  }, [knowledgeBase]);

  /**
   * Update message history limit
   */
  const updateMessageHistoryLimit = useCallback((limit) => {
    const clampedLimit = Math.min(Math.max(limit, 1), config.maxMessageHistoryLimit);
    setMessageHistoryLimit(clampedLimit);
    console.log(`📝 Message history limit set to: ${clampedLimit}`);
  }, [config.maxMessageHistoryLimit]);

  const value = {
    // Conversation state
    conversations,
    activeConversationId,
    messages,

    // UI state
    isLoading,
    showChat,
    setShowChat,
    isMinimized,
    setIsMinimized,
    showSidebar,
    setShowSidebar,

    // Other state
    knowledgeBase,
    chatLoading,
    streamingMessage,
    config,
    messageHistoryLimit,

    // Conversation actions
    loadConversation,
    startNewConversation,
    deleteConversation,
    renameConversation,

    // Message actions
    sendMessage,
    clearAllHistory,

    // Knowledge base actions
    addKnowledgeBaseDocument,
    removeKnowledgeBaseDocument,

    // Settings actions
    updateMessageHistoryLimit,

    // Legacy compatibility
    clearMessages: clearAllHistory,
    openAIKey: null,
    updateOpenAIKey: () => {},
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
